'use client';

import { Box, BoxProps } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

import styles from './ButtonBar.module.scss';
import { Tooltip } from '../Tooltip';
import { ButtonBarButtonProps, ButtonBarButton } from './ButtonBarButton';
import classNames from 'classnames';

export type ButtonBarButtonsProp = {
	prepend?: React.ReactNode;
	buttons: Record<
		string,
		ButtonBarButtonProps['buttonProps'] & { popover?: string }
	>;
	append?: React.ReactNode;
	noSideNavVisible?: boolean;
	fullWidth?: boolean;
};

export type ButtonBarProps = Omit<BoxProps, 'children'> & ButtonBarButtonsProp;

export const ButtonBar: React.FC<ButtonBarProps> = ( {
	className,
	noSideNavVisible,
	prepend,
	buttons,
	append,
	fullWidth,
	...boxProps
} ) => {
	const { isMobile } = useDeviceDetection();
	// Lets only allow one button to be clicked at a time
	const [ clickedButtonKey, setClickedButtonKey ] = useState<
		string | undefined
	>();
	const isAnyButtonLoading: boolean = Object.entries( buttons ).reduce(
		( acc, [ _, button ] ) =>
			acc ? acc : 'loading' in button ? !!button.loading : false,
		false
	);
	useEffect( () => {
		if ( !isAnyButtonLoading ) {
			setClickedButtonKey( undefined );
		}
	}, [ isAnyButtonLoading ] );

	return (
		<Box
			className={ classNames( styles.container, className, {
				[ styles.noSideNavVisible ]: noSideNavVisible || fullWidth,
			} ) }
			{ ...boxProps }
		>
			{ prepend ? <Box className={ styles.supplemental }>{ prepend }</Box> : null }
			<Box className={ styles.buttons }>
				{ Object.entries( buttons ).map(
					( [ buttonText, { popover, ...buttonProps } ] ) =>
						popover ? (
							<Tooltip
								key={ buttonText }
								target={
									<div>
										<ButtonBarButton
											buttonText={ buttonText }
											buttonProps={ buttonProps }
											isMobile={ isMobile }
											isAnyButtonLoading={ isAnyButtonLoading }
											clickedButtonKey={ clickedButtonKey }
											setClickedButtonKey={ setClickedButtonKey }
										/>
									</div>
								}
								anchorOrigin={ {
									vertical: 'top',
									horizontal: 'center',
								} }
								transformOrigin={ {
									vertical: 'bottom',
									horizontal: 'center',
								} }
							>
								{ popover }
							</Tooltip>
						) : (
							<ButtonBarButton
								key={ buttonText }
								buttonText={ buttonText }
								buttonProps={ buttonProps }
								isMobile={ isMobile }
								isAnyButtonLoading={ isAnyButtonLoading }
								clickedButtonKey={ clickedButtonKey }
								setClickedButtonKey={ setClickedButtonKey }
							/>
						)
				) }
			</Box>
			{ append }
		</Box>
	);
};
